/**
 * AI in ClientFlow: the AI Review inbox (what Claude proposes, waiting for a person), each client's
 * AI section (consent and proposals), and Firm Settings › Data and security › AI.
 */
import { ClientSummary } from "@/components/ai-buttons";
import { ConsentsPanel } from "@/components/tax-panels";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, Send, ShieldCheck, Sparkles, X } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";
import { ask, askText } from "@/components/dialogs";
import { Empty, ListSearch, Loading, matches, PageHeader, Pager, Section, Segmented, usePaged, useServerPaged } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/format";
import { hasPermission, type Me } from "@/lib/session";
import { toast } from "@/lib/use-toast";
import { cn } from "@/lib/utils";

type Proposal = {
  id: string;
  batchId: string | null;
  batchTitle: string | null;
  clientId: string | null;
  clientName: string | null;
  kind: string;
  label: string;
  outward: boolean;
  title: string;
  reason: string;
  payload: Record<string, unknown> & { display?: Record<string, string> };
  status: "pending" | "executing" | "approved" | "rejected" | "expired" | "failed";
  source: string;
  requestedBy: string | null;
  edited: boolean;
  decidedAt: string | null;
  decisionNote: string | null;
  error: string | null;
  expiresAt: string;
  createdAt: string;
};

const STATUS: Record<Proposal["status"], string> = { pending: "Waiting", executing: "In progress", approved: "Approved", rejected: "Rejected", expired: "Expired", failed: "Didn't go through" };

/** The fields a person may reword before approving, by kind. */
const EDITABLE: Record<string, Array<[string, string, "text" | "long" | "date"]>> = {
  "message.reply": [["body", "Message", "long"]],
  "request.create": [
    ["title", "What to ask for", "text"],
    ["instructions", "Instructions", "long"],
    ["dueDate", "Due", "date"],
  ],
  "task.create": [
    ["title", "Task", "text"],
    ["dueDate", "Due", "date"],
  ],
  "task.update": [["title", "Task", "text"]],
  "lead.note": [["text", "Note", "long"]],
  "lead.update": [["followUpOn", "Follow up on", "date"]],
  "cfo.action_item": [
    ["title", "Action item", "text"],
    ["dueOn", "Due", "date"],
  ],
  "invoice.draft": [["memo", "Memo", "long"]],
};

/** What the proposal would do, in plain words, from its details. */
const ORDER = ["Client", "Work", "Task", "Lead", "Conversation", "Transaction", "Date", "Payee", "Amount", "From", "To", "Class", "Status", "Assigned to"];
/** The details in a sensible order (stored records don't keep it), with dates written out. */
function displayRows(d: Record<string, string>): Array<[string, string]> {
  return Object.entries(d)
    .sort(([a], [b]) => (ORDER.indexOf(a) + 1 || 99) - (ORDER.indexOf(b) + 1 || 99))
    .map(([k, v]) => [k, /^\d{4}-\d{2}-\d{2}$/.test(v) ? formatDate(v) : v]);
}

const NOTE = "Claude's note";
/** Every detail that will be sent, by name. Ids are shown through the names ClientFlow looked up. */
const FIELD_LABELS: Record<string, string> = {
  title: "Title",
  body: "Message",
  text: "Note",
  instructions: "Instructions",
  memo: "Memo",
  type: "Kind of request",
  owner: "Who does it",
  status: "New stage",
  lostReason: "Reason lost",
  dueDate: "Due",
  dueOn: "Due",
  followUpOn: "Follow up on",
  contacted: "Marks them as contacted",
  reminderDays: "Reminders (days before due)",
  entity: "Kind of transaction",
};
const ID_FIELDS = new Set(["clientId", "engagementId", "taskId", "leadId", "threadId", "accountId", "txnId", "classId", "assigneeUserId", "assignedUserId", "display", "lines"]);
const fieldValue = (v: unknown): string | null => {
  if (v === null) return "Cleared";
  if (v === undefined || v === "") return null;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join(", ");
  const t = String(v);
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? formatDate(t) : t.replace(/_/g, " ");
};

function Details({ p }: { p: Proposal }) {
  const { [NOTE]: note, ...facts } = p.payload.display ?? {};
  const rows: Array<[string, string]> = displayRows(facts);
  for (const [k, v] of Object.entries(p.payload)) {
    if (ID_FIELDS.has(k) || (p.kind === "task.update" && k === "status" && "Status" in facts)) continue;
    const shown = fieldValue(v);
    if (shown) rows.push([FIELD_LABELS[k] ?? k, shown]);
  }
  if (p.kind === "invoice.draft" && Array.isArray(p.payload.lines)) {
    for (const l of p.payload.lines as Array<{ description: string; quantity: number; unitPriceCents: number }>) rows.push(["Line", `${l.description} · ${l.quantity} × $${(l.unitPriceCents / 100).toFixed(2)}`]);
  }
  if (!rows.length && !note) return null;
  return (
    <div className="mt-2 space-y-2">
      {rows.length > 0 && (
        <dl className="grid gap-x-4 gap-y-1 rounded-md bg-muted/40 p-3 text-sm sm:grid-cols-[max-content_1fr]">
          {rows.map(([k, v], i) => (
            <div key={i} className="contents">
              <dt className="text-xs text-muted-foreground sm:pt-0.5">{k}</dt>
              <dd className="whitespace-pre-wrap break-words">{v}</dd>
            </div>
          ))}
        </dl>
      )}
      {note && (
        <p className="break-words rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">{NOTE}:</span> {note}
        </p>
      )}
    </div>
  );
}

function EditForm({ p, onDone }: { p: Proposal; onDone: () => void }) {
  const fields = EDITABLE[p.kind] ?? [];
  const [v, setV] = useState<Record<string, string>>(Object.fromEntries(fields.map(([k]) => [k, typeof p.payload[k] === "string" ? (p.payload[k] as string) : ""])));
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => {
      const { display: _d, ...rest } = p.payload;
      const next: Record<string, unknown> = { ...rest };
      // An emptied date on a change clears it (null); on something new, it's simply left out.
      for (const [k, , type] of fields) next[k] = v[k] === "" && type !== "long" ? (type === "date" && p.kind.endsWith(".update") && p.payload[k] ? null : undefined) : v[k];
      return api.send("PATCH", `/api/ai/proposals/${p.id}`, { payload: next });
    },
    onSuccess: onDone,
  });
  return (
    <form
      className="mt-3 space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      {fields.map(([k, label, type]) => (
        <div key={k} className="space-y-1">
          <Label htmlFor={`ed-${p.id}-${k}`}>{label}</Label>
          {type === "long" ? <Textarea id={`ed-${p.id}-${k}`} rows={5} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} /> : <Input id={`ed-${p.id}-${k}`} type={type === "date" ? "date" : "text"} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} />}
        </div>
      ))}
      {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
      <div className="flex gap-2">
        <Button size="sm" type="submit" disabled={save.isPending}>
          Save changes
        </Button>
        <Button size="sm" type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function Tags({ p }: { p: Proposal }) {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{p.label}</span>
      {p.outward && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-200">{p.kind === "qbo.recategorize" ? "Changes QuickBooks" : "Client will receive it"}</span>}
      {p.edited && <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-800 dark:bg-sky-950 dark:text-sky-200">Edited</span>}
    </span>
  );
}

/** One proposal waiting for a decision. */
function ProposalCard({ p, canDecide }: { p: Proposal; canDecide: boolean }) {
  const [editing, setEditing] = useState(false);
  const approve = useMutation({
    mutationFn: () => api.send<Proposal>("POST", `/api/ai/proposals/${p.id}/approve`),
    onSuccess: (r) => (r.status === "approved" ? toast({ title: "Done", description: p.title }) : toast({ title: "It didn't go through", description: r.error ?? "" })),
  });
  const reject = useMutation({ mutationFn: (note: string) => api.send("POST", `/api/ai/proposals/${p.id}/reject`, { note }) });
  const confirmApprove = () =>
    p.outward
      ? void ask({ title: p.kind === "qbo.recategorize" ? "Change this in QuickBooks?" : "Send this to the client?", description: p.kind === "qbo.recategorize" ? "The transaction is updated in the client's QuickBooks, as when you do it yourself." : "It goes out now, from you, exactly as shown.", confirmLabel: "Approve" }).then((ok) => ok && approve.mutate())
      : approve.mutate();
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium">{p.title}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {p.clientName && p.clientId ? (
              <Link className="hover:underline" href={`/clients/${p.clientId}`}>
                {p.clientName}
              </Link>
            ) : (
              "No client"
            )}
            {" · "}
            {p.requestedBy ? `Asked by ${p.requestedBy}` : "Suggested"} {p.source === "claude" ? "in Claude" : ""} · {formatDateTime(p.createdAt)}
          </p>
        </div>
        <Tags p={p} />
      </div>
      {p.reason && <p className="mt-2 text-sm text-muted-foreground">Why: {p.reason}</p>}
      {editing ? <EditForm p={p} onDone={() => setEditing(false)} /> : <Details p={p} />}
      {p.status === "executing" && <p className="mt-2 text-sm text-amber-700">This was being carried out and didn't finish. Check whether it happened before doing it by hand.</p>}
      {canDecide && p.status === "pending" && !editing && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" disabled={approve.isPending} onClick={confirmApprove}>
            <Check className="h-4 w-4" /> Approve
          </Button>
          {(EDITABLE[p.kind] ?? []).length > 0 && (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              Edit
            </Button>
          )}
          <Button size="sm" variant="ghost" disabled={reject.isPending} onClick={() => void askText({ title: "Reject this?", label: "Note (optional)", confirmLabel: "Reject" }).then((n) => n !== null && reject.mutate(n))}>
            <X className="h-4 w-4" /> Reject
          </Button>
        </div>
      )}
      {(approve.error || reject.error) && <p className="mt-2 text-sm text-destructive">{errorMessage(approve.error ?? reject.error)}</p>}
    </div>
  );
}

/** A batch: a table to untick from, then one approval. */
function BatchCard({ items, canDecide }: { items: Proposal[]; canDecide: boolean }) {
  const first = items[0]!;
  const [off, setOff] = useState<string[]>([]);
  const pending = items.filter((p) => p.status === "pending");
  const approve = useMutation({
    mutationFn: () => api.send<{ approved: number; failed: Array<{ title: string; error: string }>; leftOut: number }>("POST", `/api/ai/batches/${first.batchId}/approve`, { exclude: off }),
    onSuccess: (r) => toast({ title: `${r.approved} done${r.leftOut ? `, ${r.leftOut} left out` : ""}`, description: r.failed.length ? `${r.failed.length} didn't go through: ${r.failed.map((f) => `${f.title} (${f.error})`).join("; ")}` : undefined }),
  });
  const reject = useMutation({ mutationFn: (note: string) => api.send("POST", `/api/ai/batches/${first.batchId}/reject`, { note }) });
  const n = pending.length - off.length;
  const outward = items.some((p) => p.outward);
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-border p-4">
        <div className="min-w-0">
          <p className="font-medium">{first.batchTitle}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {items.length} together · {first.clientName ?? "Several clients"} · {first.requestedBy ? `Asked by ${first.requestedBy}` : "Suggested"} · {formatDateTime(first.createdAt)}
          </p>
        </div>
        <Tags p={first} />
      </div>
      <ul className="divide-y divide-border">
        {items.map((p) => (
          <li key={p.id} className="flex gap-3 px-4 py-2.5 text-sm">
            {canDecide && p.status === "pending" ? (
              <input type="checkbox" aria-label={`Include ${p.title}`} className="mt-1 h-4 w-4 shrink-0" checked={!off.includes(p.id)} onChange={(e) => setOff((x) => (e.target.checked ? x.filter((y) => y !== p.id) : [...x, p.id]))} />
            ) : (
              <span className="mt-0.5 w-16 shrink-0 text-xs text-muted-foreground">{STATUS[p.status]}</span>
            )}
            <div className="min-w-0 flex-1">
              <p>{p.title}</p>
              {p.payload.display && (
                <p className="mt-0.5 break-words text-xs text-muted-foreground">
                  {displayRows(p.payload.display)
                    .filter(([k]) => k !== "To")
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(" · ")}
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
      {canDecide && pending.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-t border-border p-4">
          <Button
            size="sm"
            disabled={!n || approve.isPending}
            onClick={() => (outward ? void ask({ title: `Approve ${n}?`, description: first.kind === "qbo.recategorize" ? "Each is updated in the client's QuickBooks, one after another." : "Each goes out now, from you, exactly as shown.", confirmLabel: `Approve ${n}` }).then((ok) => ok && approve.mutate()) : approve.mutate())}
          >
            <Check className="h-4 w-4" /> Approve {n}
            {off.length ? ` (leave out ${off.length})` : ""}
          </Button>
          <Button size="sm" variant="ghost" disabled={reject.isPending} onClick={() => void askText({ title: "Reject the whole batch?", label: "Note (optional)", confirmLabel: "Reject all" }).then((note) => note !== null && reject.mutate(note))}>
            Reject all
          </Button>
        </div>
      )}
      {(approve.error || reject.error) && <p className="px-4 pb-3 text-sm text-destructive">{errorMessage(approve.error ?? reject.error)}</p>}
    </div>
  );
}

function ProposalList({ rows, canDecide }: { rows: Proposal[]; canDecide: boolean }) {
  const groups: Proposal[][] = [];
  const seen = new Map<string, Proposal[]>();
  for (const p of rows) {
    if (!p.batchId) groups.push([p]);
    else if (seen.has(p.batchId)) seen.get(p.batchId)!.push(p);
    else {
      const g = [p];
      seen.set(p.batchId, g);
      groups.push(g);
    }
  }
  // A page holds whole batches, so a batch is never split across pages.
  const page = usePaged(groups);
  return (
    <div className="space-y-3">
      {page.items.map((g) => (g.length > 1 || g[0]!.batchId ? <BatchCard key={g[0]!.batchId ?? g[0]!.id} items={g} canDecide={canDecide} /> : <ProposalCard key={g[0]!.id} p={g[0]!} canDecide={canDecide} />))}
      <Pager paged={page} noun="proposals" />
    </div>
  );
}

/** AI Review: everything Claude has proposed, waiting for a person. */
export function AiReviewPage({ me }: { me: Me }) {
  const [view, setView] = useState<"pending" | "decided">("pending");
  const sp = useServerPaged(view);
  // Waiting proposals come complete (batches stay together); decided ones a page at a time.
  const { data: res, isLoading } = useQuery<{ items: Proposal[]; total: number }>({ queryKey: [view === "pending" ? "/api/ai/proposals?view=pending" : `/api/ai/proposals?view=decided&offset=${sp.offset}&limit=${sp.size}`], placeholderData: (prev, prevQuery) => (view === "decided" && String(prevQuery?.queryKey[0]).includes("view=decided") ? prev : undefined) });
  const data = res?.items;
  const canDecide = hasPermission(me, "clients.write");
  return (
    <>
      <PageHeader title="AI Review" description="What Claude has proposed. Nothing happens until someone approves it here; you can edit the wording first, or reject it." />
      <Segmented
        label="Proposals"
        className="mb-4"
        value={view}
        onChange={setView}
        options={[
          { value: "pending", label: "Waiting" },
          { value: "decided", label: "Decided" },
        ]}
      />
      {isLoading ? (
        <Loading />
      ) : !data?.length ? (
        <Empty>{view === "pending" ? "Nothing waiting. When Claude proposes something, it appears here for you to approve." : "Nothing decided yet."}</Empty>
      ) : view === "pending" ? (
        <ProposalList rows={data} canDecide={canDecide} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-card-border bg-card shadow-[var(--card-shadow)]">
          <ul className="divide-y divide-border">
            {data.map((p) => (
              <li key={p.id} className="flex flex-col gap-1 px-4 py-3 text-sm sm:flex-row sm:items-center sm:gap-4">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{p.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {p.clientName ?? "No client"} · {p.label}
                    {p.decisionNote ? ` · "${p.decisionNote}"` : ""}
                    {p.error ? ` · ${p.error}` : ""}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground">{p.decidedAt ? formatDateTime(p.decidedAt) : ""}</span>
                <span className={cn("w-fit rounded-full px-2 py-0.5 text-xs font-medium", p.status === "approved" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" : p.status === "failed" ? "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200" : "bg-muted text-muted-foreground")}>{STATUS[p.status]}</span>
              </li>
            ))}
          </ul>
          <Pager paged={sp.paged(res?.total ?? 0)} noun="proposals" className="px-4 pb-3" />
        </div>
      )}
    </>
  );
}

type Consent = { id: string; name: string; status: string; consentedAt: string | null; expiresAt: string | null; sentAt: string | null; canSend: boolean };

function SendConsent({ clientId, label = "Send AI consent" }: { clientId: string; label?: string }) {
  const send = useMutation({ mutationFn: () => api.send("POST", `/api/clients/${clientId}/ai-consent`), onSuccess: () => toast({ title: "AI consent sent", description: "They'll find it in their portal to sign." }) });
  return (
    <>
      <Button size="sm" variant="outline" disabled={send.isPending} onClick={() => send.mutate()}>
        <Send className="h-4 w-4" /> {label}
      </Button>
      {send.error && <p className="w-full text-xs text-destructive">{errorMessage(send.error)}</p>}
    </>
  );
}

/** On a client's page: their AI consent, and anything Claude proposed for them. */
export function ClientAi({ clientId, canEdit, me }: { clientId: string; canEdit: boolean; me: Me }) {
  const { data: consents } = useQuery<{ clients: Consent[] }>({ queryKey: ["/api/ai/consents"] });
  const { data: props } = useQuery<{ items: Proposal[]; total: number }>({ queryKey: [`/api/ai/proposals?view=pending&clientId=${clientId}&limit=1`] });
  const c = consents?.clients.find((x) => x.id === clientId);
  return (
    <div className="space-y-3 text-sm">
      {!consents ? (
        <Loading />
      ) : c?.consentedAt ? (
        <p className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
          <span>
            AI consent signed {formatDate(c.consentedAt)}
            {c.expiresAt ? `, until ${formatDate(c.expiresAt)}` : ""}. Claude and the AI readers can work with their information.
          </span>
        </p>
      ) : (
        <div className="space-y-2">
          <p className="text-muted-foreground">{c?.sentAt ? `AI consent sent ${formatDate(c.sentAt)}, waiting for their signature.` : "No AI consent yet: Claude and the AI readers can't work with their information."}</p>
          {canEdit && !c?.sentAt && (
            <div className="flex flex-wrap items-center gap-2">
              {c?.canSend ? <SendConsent clientId={clientId} /> : <span className="text-xs text-muted-foreground">Invite them to the client portal first, or an owner or admin can record a paper-signed consent under Consent records below.</span>}
            </div>
          )}
        </div>
      )}
      {c?.consentedAt && <ClientSummary clientId={clientId} />}
      <details className="group rounded-md border border-border">
        <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium">Consent records (§7216)</summary>
        <div className="border-t border-border p-3">
          <ConsentsPanel clientId={clientId} me={me} />
        </div>
      </details>
      {!!props?.total && (
        <p>
          <Link className="font-medium text-primary hover:underline" href="/ai">
            {props.total} AI proposal{props.total === 1 ? "" : "s"} waiting for review
          </Link>
        </p>
      )}
    </div>
  );
}

type Settings = {
  settings: { enabled: boolean; blockedUserIds: string[]; consentText: string; connector: "off" | "read" | "propose"; playbook: string; readDocuments: boolean; askRetake: boolean };
  connectorUrl: string;
  /** Who ticked "Our Claude account processes data in the US only", and when; null until ticked. */
  usOnly: { at: string; byUserId: string; byName: string } | null;
  /** The connector may be switched on (ticked, or the demo firm). */
  connectorAllowed: boolean;
  isDemo: boolean;
  canConfirmUsOnly: boolean;
  requireConsent: boolean;
  aiConfigured: boolean;
  staff: Array<{ userId: string; name: string; role: string }>;
  actions: Array<{ kind: string; label: string; outward: boolean }>;
  never: string[];
  proposalDays: number;
  canManage: boolean;
};

/** Firm Settings › Data and security › AI. */
export function AiSettingsPanel() {
  const { data } = useQuery<Settings>({ queryKey: ["/api/ai/settings"] });
  if (!data) return <Loading />;
  return (
    <div className="space-y-6">
      <Switches data={data} />
      <Connector data={data} />
      <Section title="What Claude may do" description={`Claude reads what the person using it may see in ClientFlow, and explains it. Everything below is only proposed: it waits in Review › AI proposals until someone approves it (or it expires after ${data.proposalDays} days).`}>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <p className="mb-2 text-sm font-medium">Can propose</p>
            <ul className="space-y-1.5 text-sm">
              {data.actions.map((a) => (
                <li key={a.kind} className="flex items-start gap-2">
                  <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                  <span>
                    {a.label}
                    {a.outward && <span className="text-xs text-muted-foreground"> · reaches the client or QuickBooks</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="mb-2 text-sm font-medium">Never, not even as a proposal</p>
            <ul className="space-y-1.5 text-sm">
              {data.never.map((n) => (
                <li key={n} className="flex items-start gap-2">
                  <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>
      <ConsentSection data={data} />
      <Activity staff={data.staff} canManage={data.canManage} />
    </div>
  );
}

type Connection = { id: string; userId: string; userName: string; appName: string; createdAt: string; lastUsedAt: string | null };

const MODES: Array<["off" | "read" | "propose", string, string]> = [
  ["off", "Off", "Claude can't connect."],
  ["read", "Read only", "Claude reads and explains what each person can see. Start here."],
  ["propose", "Read and propose", "Claude can also propose actions; each waits in Review › AI proposals for a person to approve."],
];

/**
 * "Our Claude account processes data in the US only": the owner's tick, needed before the connector can
 * be switched on (the clients' AI consent doesn't cover processing outside the US). Un-ticking while the
 * connector is on turns it Off, after a confirmation.
 */
function UsOnly({ data }: { data: Settings }) {
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: (confirmed: boolean) => api.send<{ ok: true; connectorTurnedOff: boolean }>("POST", "/api/ai/settings/us-only", { confirmed }),
    onSuccess: (r, confirmed) => toast({ title: confirmed ? "Recorded" : r.connectorTurnedOff ? "Recorded; the Claude connector is now Off" : "Recorded" }),
  });
  const ticked = !!data.usOnly;
  const change = async (confirmed: boolean) => {
    if (!confirmed && data.settings.connector !== "off" && !data.isDemo) {
      const ok = await ask({
        title: "Turn the Claude connector off?",
        description: "Without this confirmation the connector can't stay on, so it is switched Off now. Your team's Claude stops reaching ClientFlow until it is ticked again and the connector is switched back on.",
        confirmLabel: "Turn off",
        destructive: true,
      });
      if (!ok) return;
    }
    save.mutate(confirmed);
  };
  return (
    <div className="space-y-2 rounded-lg border border-border p-3">
      <label className={cn("flex items-start gap-2.5", data.canConfirmUsOnly ? "cursor-pointer" : "cursor-not-allowed")}>
        <input type="checkbox" className="mt-0.5 h-4 w-4" checked={ticked} disabled={!data.canConfirmUsOnly || save.isPending} onChange={(e) => void change(e.target.checked)} />
        <span>
          <span className="font-medium">Our Claude account processes data in the US only</span>
          <span className="block text-xs text-muted-foreground">
            Tick this only once the firm's Claude account keeps all processing in the US (US-only inference). The clients' AI consent says nothing about processing outside the US, so the connector stays Off until this is ticked.
          </span>
        </span>
      </label>
      <p className="pl-[1.625rem] text-xs text-muted-foreground">
        {ticked ? `Confirmed by ${data.usOnly!.byName || "the owner"} on ${formatDateTime(data.usOnly!.at)}. Each change is in the audit log.` : data.isDemo ? "Demo firm: not needed here; its clients and data are made up." : "Not confirmed yet."}
        {!data.canConfirmUsOnly && " Only the owner can change this."}
      </p>
      {save.error && <p className="pl-[1.625rem] text-xs text-destructive">{errorMessage(save.error)}</p>}
    </div>
  );
}

/** The Claude connector: its mode, the address to add in Claude, who's connected, and the firm playbook. */
function Connector({ data }: { data: Settings }) {
  const save = useMutation({ meta: { inlineError: true }, mutationFn: (body: Record<string, unknown>) => api.send("PATCH", "/api/ai/settings", body), onSuccess: () => toast({ title: "Saved" }) });
  const { data: conns } = useQuery<Connection[]>({ queryKey: ["/api/ai/connections"] });
  const revoke = useMutation({ mutationFn: (id: string) => api.send("POST", `/api/ai/connections/${id}/revoke`), onSuccess: () => toast({ title: "Disconnected" }) });
  const [playbook, setPlaybook] = useState(data.settings.playbook);
  const [editing, setEditing] = useState(false);
  const mode = data.settings.connector;
  return (
    <Section title="Claude connector" description="Lets your team use Claude (on the firm's Claude Team account) with ClientFlow. Each person connects with their own ClientFlow sign-in and sees only what they can see here.">
      <div className="space-y-5 text-sm">
        <UsOnly data={data} />
        <fieldset disabled={!data.canManage || save.isPending} className="grid gap-2 sm:grid-cols-3">
          {MODES.map(([k, label, help]) => (
            <label
              key={k}
              className={cn("flex items-start gap-2 rounded-lg border p-3", mode === k ? "border-primary bg-primary/5" : "border-border", k !== "off" && !data.connectorAllowed ? "cursor-not-allowed opacity-60" : "cursor-pointer")}
            >
              <input type="radio" name="connector" className="mt-0.5" checked={mode === k} disabled={k !== "off" && !data.connectorAllowed} onChange={() => save.mutate({ connector: k })} />
              <span>
                <span className="font-medium">{label}</span>
                <span className="block text-xs text-muted-foreground">{help}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {!data.connectorAllowed && <p className="text-xs text-muted-foreground">Read only and Read and propose become available once the owner ticks the box above.</p>}
        {save.error && !editing && <p className="text-xs text-destructive">{errorMessage(save.error)}</p>}
        {mode !== "off" && (
          <div className="space-y-2">
            <p className="font-medium">Connection address</p>
            <div className="flex min-w-0 items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded bg-muted px-2 py-1.5 text-xs">{data.connectorUrl}</code>
              <Button size="sm" variant="outline" onClick={() => void navigator.clipboard.writeText(data.connectorUrl).then(() => toast({ title: "Copied" }))}>
                Copy
              </Button>
            </div>
            <details className="rounded-md bg-muted/40 p-3">
              <summary className="cursor-pointer font-medium">How to add it in Claude</summary>
              <ol className="mt-2 space-y-1.5 text-xs">
                <li>
                  <span className="font-medium">Step 1 (Owner of the Claude Team account, once):</span> in claude.ai, open <span className="font-medium">Admin settings › Connectors</span>, click <span className="font-medium">Add</span> › <span className="font-medium">Custom</span>, name it ClientFlow, paste the address above, and save.
                </li>
                <li>
                  <span className="font-medium">Step 2 (each person):</span> in Claude, open <span className="font-medium">Customize › Connectors</span> (or Settings › Connectors), find ClientFlow and click <span className="font-medium">Connect</span>.
                </li>
                <li>
                  <span className="font-medium">Step 3:</span> ClientFlow's sign-in opens: sign in with your two-step code and click <span className="font-medium">Allow</span>.
                </li>
                <li>
                  <span className="font-medium">Step 4:</span> in a Claude chat, ask something like "What needs my attention today in ClientFlow?"
                </li>
              </ol>
            </details>
          </div>
        )}
        <div>
          <p className="mb-2 font-medium">Connected</p>
          {!conns?.length ? (
            <p className="text-muted-foreground">Nobody has connected Claude yet.</p>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border">
              {conns.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                  <span className="min-w-0">
                    <span className="font-medium">{c.userName}</span> <span className="text-xs text-muted-foreground">· {c.appName} · since {formatDate(c.createdAt)}{c.lastUsedAt ? ` · last used ${formatDateTime(c.lastUsedAt)}` : ""}</span>
                  </span>
                  <Button size="sm" variant="ghost" className="text-destructive" disabled={revoke.isPending} onClick={() => void ask({ title: `Disconnect ${c.userName}'s Claude?`, description: "It stops working at once. They can connect again later.", confirmLabel: "Disconnect", destructive: true }).then((ok) => ok && revoke.mutate(c.id))}>
                    Disconnect
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-medium">Firm playbook</p>
            {data.canManage && !editing && (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                Edit playbook
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">Claude reads this at the start of every conversation: how the firm works and writes to clients. {"{firm}"} becomes the firm's name.</p>
          {editing ? (
            <>
              <Textarea rows={18} className="text-xs" value={playbook} onChange={(e) => setPlaybook(e.target.value)} aria-label="Playbook" />
              {save.error && <p className="text-destructive">{errorMessage(save.error)}</p>}
              <div className="flex gap-2">
                <Button size="sm" disabled={save.isPending} onClick={() => save.mutate({ playbook }, { onSuccess: () => setEditing(false) })}>
                  Save playbook
                </Button>
                <Button size="sm" variant="ghost" onClick={() => (setPlaybook(data.settings.playbook), setEditing(false))}>
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <details>
              <summary className="cursor-pointer text-primary">Read the playbook</summary>
              <p className="mt-2 whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-xs">{data.settings.playbook}</p>
            </details>
          )}
        </div>
      </div>
    </Section>
  );
}

function Switches({ data }: { data: Settings }) {
  const save = useMutation({ mutationFn: (body: Record<string, unknown>) => api.send("PATCH", "/api/ai/settings", body), onSuccess: () => toast({ title: "AI settings saved" }) });
  const s = data.settings;
  return (
    <Section title="AI for the firm" description={data.aiConfigured ? "Covers the AI readers (client documents, bills, statements, returns), CFO commentary drafts, and Claude." : "AI isn't set up on this server yet (ANTHROPIC_API_KEY in Replit Secrets). These settings apply once it is."}>
      <fieldset disabled={!data.canManage || save.isPending} className="space-y-4 text-sm">
        <label className="flex items-start gap-2.5">
          <input type="checkbox" className="mt-0.5 h-4 w-4" checked={s.enabled} onChange={(e) => save.mutate({ enabled: e.target.checked })} />
          <span>
            <span className="font-medium">Use AI</span>
            <span className="block text-xs text-muted-foreground">Off: every AI feature stops, and nothing waiting in Review › AI proposals can be approved.</span>
          </span>
        </label>
        <label className="flex items-start gap-2.5">
          <input type="checkbox" className="mt-0.5 h-4 w-4" checked={s.readDocuments} onChange={(e) => save.mutate({ readDocuments: e.target.checked })} />
          <span>
            <span className="font-medium">Read clients' uploaded documents</span>
            <span className="block text-xs text-muted-foreground">Says what each upload is (W-2, 1099, 1098...), its tax year and whose it is, and copies its boxes for Review › Documents. A person confirms before anything is filed or used.</span>
          </span>
        </label>
        <label className="flex items-start gap-2.5">
          <input type="checkbox" className="mt-0.5 h-4 w-4" checked={s.askRetake} onChange={(e) => save.mutate({ askRetake: e.target.checked })} />
          <span>
            <span className="font-medium">Ask clients to retake unreadable photos</span>
            <span className="block text-xs text-muted-foreground">When a photo is too blurry to read, or a PDF has a password, the client gets a request for a usable copy straight away.</span>
          </span>
        </label>
        <div>
          <p className="mb-1.5 font-medium">Who may use it</p>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {data.staff.map((m) => (
              <label key={m.userId} className="flex items-center gap-2">
                <input type="checkbox" checked={!s.blockedUserIds.includes(m.userId)} onChange={(e) => save.mutate({ blockedUserIds: e.target.checked ? s.blockedUserIds.filter((x) => x !== m.userId) : [...s.blockedUserIds, m.userId] })} /> {m.name}
              </label>
            ))}
          </div>
        </div>
        <p className="flex items-start gap-2 rounded-md bg-muted/50 px-3 py-2 text-xs">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
          {data.requireConsent ? "Each client's signed AI consent is required before AI works with their information. This can't be turned off." : "Demo firm: consent isn't required until the demo's AI scenario turns it on."}
        </p>
      </fieldset>
      {!data.canManage && <p className="mt-3 text-xs text-muted-foreground">Only owners and admins can change these.</p>}
    </Section>
  );
}

function ConsentSection({ data }: { data: Settings }) {
  const { data: list } = useQuery<{ clients: Consent[] }>({ queryKey: ["/api/ai/consents"] });
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(data.settings.consentText);
  const save = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("PATCH", "/api/ai/settings", { consentText: text }), onSuccess: () => (setEditing(false), toast({ title: "Consent wording saved" })) });
  const clients = list?.clients ?? [];
  const signed = clients.filter((c) => c.consentedAt).length;
  const [search, setSearch] = useState("");
  const found = clients.filter((c) => matches(search, c.name));
  const page = usePaged(found, { resetKey: search });
  return (
    <Section title="AI consent (§7216)" description="Each client signs it once in their portal; it lasts a year and they can withdraw it any time. Have your compliance advisor review the wording before you send it.">
      <div className="space-y-4 text-sm">
        {editing ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              {"{firm}"} and {"{client}"} are filled in for each client, and {"{expires}"} with the date the consent expires (one year after it is sent), which is also the date ClientFlow records. Keep the paragraphs the IRS requires (they start "Federal law requires…", "You are not required…" and "If you believe…").
            </p>
            <Textarea rows={16} className="text-xs" value={text} onChange={(e) => setText(e.target.value)} aria-label="Consent wording" />
            {save.error && <p className="text-destructive">{errorMessage(save.error)}</p>}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
                Save wording
              </Button>
              <Button size="sm" variant="ghost" onClick={() => (setText(data.settings.consentText), setEditing(false))}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <details className="min-w-0 flex-1">
              <summary className="cursor-pointer text-primary">Read the consent wording</summary>
              <p className="mt-2 whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-xs">{data.settings.consentText}</p>
            </details>
            {data.canManage && (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                Edit wording
              </Button>
            )}
          </div>
        )}
        <div>
          <p className="mb-2 font-medium">
            {signed} of {clients.length} active clients and prospects have signed
          </p>
          {clients.length > 10 && <ListSearch className="mb-2" value={search} onChange={setSearch} placeholder="Search clients" />}
          {search && !found.length && <p className="text-muted-foreground">No clients match "{search.trim()}".</p>}
          <ul className="divide-y divide-border rounded-md border border-border empty:hidden">
            {page.items.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                <Link className="min-w-0 truncate hover:underline" href={`/clients/${c.id}`}>
                  {c.name}
                </Link>
                {c.consentedAt ? (
                  <span className="text-xs text-emerald-700 dark:text-emerald-300">Signed {formatDate(c.consentedAt)}</span>
                ) : c.sentAt ? (
                  <span className="text-xs text-muted-foreground">Sent {formatDate(c.sentAt)}</span>
                ) : c.canSend ? (
                  <SendConsent clientId={c.id} label="Send" />
                ) : (
                  <span className="text-xs text-muted-foreground">No portal account</span>
                )}
              </li>
            ))}
          </ul>
          <Pager paged={page} noun="clients" className="mt-2" compact />
        </div>
      </div>
    </Section>
  );
}

type ActivityRow = { id: number; action: string; userName: string | null; clientName: string | null; clientId: string | null; summary: string; outcome: "ok" | "refused" | "error"; source: string; createdAt: string };

function Activity({ staff, canManage }: { staff: Settings["staff"]; canManage: boolean }) {
  const [userId, setUserId] = useState("");
  const [pages, setPages] = useState<number[]>([]);
  const qs = (before?: number) => `/api/ai/activity?${new URLSearchParams({ ...(userId ? { userId } : {}), ...(before ? { before: String(before) } : {}) })}`;
  const { data } = useQuery<{ rows: ActivityRow[]; next: number | null }>({ queryKey: [qs(pages.at(-1))] });
  return (
    <Section
      title="AI activity"
      description={canManage ? "Everything AI looked at or drafted, by whom, for which client, and anything it was refused." : "What you've used AI for."}
      action={
        canManage && (
          <select aria-label="Person" className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={userId} onChange={(e) => (setUserId(e.target.value), setPages([]))}>
            <option value="">Everyone</option>
            {staff.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.name}
              </option>
            ))}
          </select>
        )
      }
    >
      {!data ? (
        <Loading />
      ) : !data.rows.length ? (
        <p className="text-sm text-muted-foreground">Nothing yet.</p>
      ) : (
        <>
          <ul className="-m-5 divide-y divide-border">
            {data.rows.map((a) => (
              <li key={a.id} className="px-5 py-2.5 text-sm">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <span className="min-w-0 break-words">{a.summary || a.action}</span>
                  <span className={cn("text-xs", a.outcome === "refused" ? "font-medium text-amber-700 dark:text-amber-300" : a.outcome === "error" ? "text-destructive" : "text-muted-foreground")}>{a.outcome === "refused" ? "Refused" : a.outcome === "error" ? "Error" : ""}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {formatDateTime(a.createdAt)} · {a.userName ?? "Scheduled"}
                  {a.clientName ? ` · ${a.clientName}` : ""} · {a.source === "claude" ? "Claude" : a.source === "demo" ? "Demo" : "In ClientFlow"}
                </p>
              </li>
            ))}
          </ul>
          <div className="mt-5 flex gap-2">
            {pages.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => setPages((p) => p.slice(0, -1))}>
                Newer
              </Button>
            )}
            {data.next && (
              <Button size="sm" variant="outline" onClick={() => setPages((p) => [...p, data.next!])}>
                Older
              </Button>
            )}
          </div>
        </>
      )}
    </Section>
  );
}
